import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { from, Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, timeout, map, switchMap } from 'rxjs/operators';

export class ApiResponse<T> {
constructor(
    public statusCode: number,
    public data?: T,
    public error?: string
) {}

get isSuccess(): boolean {
    return this.statusCode >= 200 && this.statusCode < 300;
}

get isError(): boolean {
    return this.statusCode >= 400;
}
}

@Injectable({
providedIn: 'root'
})
export class NetworkService {

private timeoutDuration = 10000;

constructor(private http: HttpClient) { }

async getHeaders(): Promise<HttpHeaders> {
    const authToken = localStorage.getItem('auth_token');
    let headers = new HttpHeaders({'Content-Type': 'application/json',
    });
    if (authToken) {headers = headers.set('Authorization', `Bearer ${authToken}`);
    }
    return headers;
}

    getPublicHeaders(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
        });
    }

get<T>(url: string, customHeaders?: HttpHeaders): Observable<ApiResponse<T>> {
    const headers$ = customHeaders ? Promise.resolve(customHeaders) : this.getHeaders();
    return from(headers$).pipe(
        switchMap(headers => this.http.get<T>(url, { headers, observe: 'response' })
        ), timeout(this.timeoutDuration), map(response => new ApiResponse<T>(response.status, response.body as T)),catchError(this.handleError)
    );
}

    post<T>(url: string, body: any, customHeaders?: HttpHeaders): Observable<ApiResponse<T>> {
        const headers$ = customHeaders ? Promise.resolve(customHeaders) : this.getHeaders();
        return from(headers$).pipe(
            switchMap( headers =>
                this.http.post<T>(url, body, { headers, observe: 'response' })
            ),
            timeout(this.timeoutDuration),
            map(response => new ApiResponse<T>(response.status, response.body as T)),
            catchError(this.handleError)
        );
    }

    put<T>(url: string, body: any, customHeaders?: HttpHeaders): Observable<ApiResponse<T>> {
        const headers$ = customHeaders ? Promise.resolve(customHeaders) : this.getHeaders();
        return from(headers$).pipe(
            switchMap(headers =>
                this.http.put<T>(url, body, { headers, observe: 'response' })
            ),
            timeout(this.timeoutDuration),
            map(response => new ApiResponse<T>(response.status, response.body as T)),
            catchError(this.handleError)
        );
    }

    delete<T>(url: string, customHeaders?: HttpHeaders): Observable<ApiResponse<T>> {
        const headers$ = customHeaders ? Promise.resolve(customHeaders) : this.getHeaders();
        return from(headers$).pipe(
            switchMap( headers =>
                this.http.delete<T>(url, { headers, observe: 'response' })
            ),
            timeout(this.timeoutDuration),
            map(response => new ApiResponse<T>(response.status, response.body as T)),
            catchError(this.handleError)
        );
    }

    private handleError(error: HttpErrorResponse | TimeoutError): Observable<ApiResponse<any>> {
    let apiResponse: ApiResponse<any>;

    if (error instanceof HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
        apiResponse = new ApiResponse(error.status, undefined, `Client error: ${error.error.message}`);
    } else {
        let errorMessage = 'Unknown server error';
        if (error.error && typeof error.error === 'object' && error.error.message)
        {
        errorMessage = error.error.message;
        } else if(error.statusText){
        errorMessage = error.statusText;
        }
        apiResponse = new ApiResponse(error.status, undefined, errorMessage);

    }
    } else if (error instanceof TimeoutError) {
    apiResponse = new ApiResponse(408, undefined, 'Request timed out');
    } else {
    apiResponse = new ApiResponse(500, undefined, `An unexpected error occurred: ${error}`);
    }

    return throwError(() => apiResponse);
}
}